import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box, Grid, CircularProgress, Table, TableHead, TableRow,
  TableCell, TableBody, Typography, Alert, Chip, Tabs, Tab,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { useHistory, useParams, useRouteMatch, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import PageHeader from '../../components/PageHeader';
import SectionCard from '../../components/SectionCard';
import StatusChip from '../../components/StatusChip';
import InvButton from '../../components/InvButton';
import ConfirmDialog from '../../components/ConfirmDialog';
import RfqQuoteComparisonTable from '../../components/material/RfqQuoteComparisonTable';
import RfqEvaluationPanel from '../../components/material/RfqEvaluationPanel';
import RfqAwardDialog from '../../components/material/RfqAwardDialog';
import { DetailRow } from '../../components/InvField';
import {
  getRfq, getRfqItems, getRfqQuotes, getRfqSummary,
  getRfqQuoteComparison, getRfqEvaluationSummary,
  publishRfq, closeRfq, cancelRfq,
} from '../../api/rfq';
import { getPurchasableVendors } from '../../api/vendor';
import { getUoms } from '../../api/inventoryMaster';
import useItemNames from '../../hooks/useItemNames';
import {
  fromApiRfq, fromApiRfqItem, getRfqWorkflowIndex, isRfqPublished,
  vendorDisplayLabel, normalizeApiList, canAcceptQuote,
} from '../../utils/rfqEntity';
import { normalizeUomList } from '../../utils/masterEntity';
import { hasDisplayValue } from '../../utils/poLifecycle';
import { RFQ_WORKFLOW_STAGES } from '../../constants/rfqManagement';
import { formatDate, formatCurrency } from '../../utils';
import useInvRole from '../../hooks/useInvRole';
import { INV_ACTIONS } from '../../utils/inventoryRoles';
import { IPD_COLORS } from '../../theme';

function buildUomMap(uoms) {
  const map = {};
  (Array.isArray(uoms) ? uoms : []).forEach((u) => {
    const id = u.uom_id || u.id;
    if (!id) return;
    map[id] = u.uom_name || u.uom_code || u.name || '';
  });
  return map;
}

function resolveUomLabel(item, uomMap = {}) {
  if (hasDisplayValue(item?.uom_name)) return item.uom_name;
  if (hasDisplayValue(item?.uom_code) && !/^[0-9a-f-]{36}$/i.test(item.uom_code)) {
    return item.uom_code;
  }
  if (item?.uom_id && uomMap[item.uom_id]) return uomMap[item.uom_id];
  return '—';
}

function RfqWorkflowBar({ status }) {
  const currentIndex = getRfqWorkflowIndex(status);
  return (
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap',
      mb: 2.5, p: { xs: 1.5, sm: 2 }, borderRadius: '12px',
      border: `1px solid ${IPD_COLORS.border}`, backgroundColor: IPD_COLORS.cardBg,
    }}>
      {RFQ_WORKFLOW_STAGES.map((stage, i) => {
        const isCompleted = currentIndex > i;
        const isActive = currentIndex === i || (status === 'EVALUATED' && stage.key === 'CLOSED');
        return (
          <React.Fragment key={stage.key}>
            <Chip label={stage.label} size="small"
              sx={{
                fontWeight: 700,
                backgroundColor: isCompleted || isActive ? IPD_COLORS.primary : IPD_COLORS.selectedBg,
                color: isCompleted || isActive ? '#fff' : IPD_COLORS.textSecondary,
              }} />
            {i < RFQ_WORKFLOW_STAGES.length - 1 && (
              <Typography sx={{ color: IPD_COLORS.textSecondary, fontSize: 12, display: { xs: 'none', sm: 'inline' } }}>→</Typography>
            )}
          </React.Fragment>
        );
      })}
      <Box sx={{ flex: { xs: '1 1 100%', sm: 1 }, display: { xs: 'none', sm: 'block' } }} />
      <StatusChip status={status === 'OPEN' ? 'PUBLISHED' : status} />
    </Box>
  );
}

const TAB_OVERVIEW = 'overview';
const TAB_QUOTES = 'quotes';
const TAB_COMPARISON = 'comparison';
const TAB_EVALUATION = 'evaluation';

/** ConfirmDialog action keys on this screen -> the permission each one needs. */
const DIALOG_ACTION_PERMISSION = {
  publish: INV_ACTIONS.RFQ_PUBLISH,
  close: INV_ACTIONS.RFQ_CLOSE,
  cancel: INV_ACTIONS.RFQ_CANCEL,
};

export default function RfqDetail() {
  const { id } = useParams();
  const { url } = useRouteMatch();
  const history = useHistory();
  const { can } = useInvRole();
  const location = useLocation();
  const { resolveItem } = useItemNames();

  const [rfq, setRfq] = useState(null);
  const [rfqItems, setRfqItems] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [summary, setSummary] = useState(null);
  const [vendorDirectory, setVendorDirectory] = useState([]);
  const [uomMap, setUomMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [actioning, setActioning] = useState(false);
  const [activeTab, setActiveTab] = useState(TAB_OVERVIEW);

  const [comparison, setComparison] = useState(null);
  const [comparisonLoading, setComparisonLoading] = useState(false);
  const [comparisonError, setComparisonError] = useState(null);

  const [evaluation, setEvaluation] = useState(null);
  const [evaluationLoading, setEvaluationLoading] = useState(false);
  const [evaluationError, setEvaluationError] = useState(null);

  const [awardTarget, setAwardTarget] = useState(null);

  const query = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const justSaved = query.get('saved') === '1';
  const justPublished = query.get('published') === '1';
  const editUrl = `${url}/edit`;
  const poBaseUrl = url.replace(/\/rfqs\/[^/]+$/, '/po');

  const loadCore = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [r, items, q, s, vendors, uoms] = await Promise.all([
        getRfq(id),
        getRfqItems(id).catch(() => []),
        getRfqQuotes(id).catch(() => []),
        getRfqSummary(id).catch(() => null),
        getPurchasableVendors().catch(() => []),
        getUoms({ is_active: true, limit: 200 }).catch(() => []),
      ]);
      setRfq(fromApiRfq(r));
      setRfqItems(normalizeApiList(items).map(fromApiRfqItem));
      setQuotes(normalizeApiList(q));
      setSummary(s);
      setVendorDirectory(vendors);
      setUomMap(buildUomMap(normalizeUomList(uoms)));
    } catch (e) {
      setError(e.message || 'Failed to load RFQ');
    } finally {
      setLoading(false);
    }
  }, [id]);

  const loadComparison = useCallback(async () => {
    setComparisonLoading(true);
    setComparisonError(null);
    try {
      const data = await getRfqQuoteComparison(id);
      setComparison(data);
    } catch (e) {
      setComparisonError(e.message || 'Failed to load comparison');
    } finally {
      setComparisonLoading(false);
    }
  }, [id]);

  const loadEvaluation = useCallback(async () => {
    setEvaluationLoading(true);
    setEvaluationError(null);
    try {
      const data = await getRfqEvaluationSummary(id);
      setEvaluation(data);
    } catch (e) {
      setEvaluationError(e.message || 'Failed to load evaluation');
    } finally {
      setEvaluationLoading(false);
    }
  }, [id]);

  useEffect(() => { loadCore(); }, [loadCore]);

  useEffect(() => {
    if (activeTab === TAB_COMPARISON && !comparison && !comparisonLoading) loadComparison();
    if (activeTab === TAB_EVALUATION && !evaluation && !evaluationLoading) loadEvaluation();
  }, [activeTab, comparison, comparisonLoading, evaluation, evaluationLoading, loadComparison, loadEvaluation]);

  const invitedVendors = useMemo(() => {
    const ids = rfq?.invited_vendor_ids || [];
    return ids.map((vid) => {
      const match = vendorDirectory.find((v) => (v.vendor_id || v.id) === vid);
      return match ? { id: vid, label: vendorDisplayLabel(match) } : { id: vid, label: vid };
    });
  }, [rfq, vendorDirectory]);

  async function executeAction() {
    if (!dialog) return;
    const required = DIALOG_ACTION_PERMISSION[dialog.action];
    if (required && !can(required)) return;
    setActioning(true);
    try {
      if (dialog.action === 'publish') {
        await publishRfq(id);
        toast.success('RFQ published. Vendors will be notified.');
      } else if (dialog.action === 'close') {
        await closeRfq(id);
        toast.success('RFQ closed for new quotes.');
      } else if (dialog.action === 'cancel') {
        await cancelRfq(id);
        toast.success('RFQ cancelled.');
      }
      setDialog(null);
      if (justSaved || justPublished) history.replace(url);
      await loadCore();
    } catch (e) {
      toast.error(e.message || 'Action failed');
      setDialog(null);
    } finally {
      setActioning(false);
    }
  }

  function handleAwardSuccess({ poId }) {
    toast.success(poId ? 'Quote accepted and PO created.' : 'Quote accepted. RFQ awarded.');
    setAwardTarget(null);
    if (poId) {
      history.push(`${poBaseUrl}/${poId}`);
    } else {
      loadCore();
      loadEvaluation();
    }
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}>
        <CircularProgress sx={{ color: IPD_COLORS.primary }} />
      </Box>
    );
  }
  if (error) return <Box sx={{ p: 4 }}><Alert severity="error">{error}</Alert></Box>;
  if (!rfq) return null;

  const status = rfq.status || 'DRAFT';
  const published = isRfqPublished(status);
  const showQuotesTab = published || ['CLOSED', 'EVALUATED', 'AWARDED'].includes(status);
  const showComparisonTab = showQuotesTab && quotes.length > 0;
  const showEvaluationTab = ['CLOSED', 'EVALUATED', 'AWARDED'].includes(status);

  return (
    <Box>
      <PageHeader
        eyebrow="Material Management › RFQs"
        title={rfq.rfq_number}
        description={rfq.rfq_title || rfq.title}
        actions={(
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            <InvButton variant="outlined" tone="teal" size="small" onClick={() => history.goBack()}>
              Back
            </InvButton>
            {status === 'DRAFT' && (
              <>
                {can(INV_ACTIONS.RFQ_EDIT) && (
                  <InvButton size="small" variant="outlined" tone="teal" startIcon={<EditIcon />}
                    onClick={() => history.push(editUrl)}>
                    Edit
                  </InvButton>
                )}
                {can(INV_ACTIONS.RFQ_PUBLISH) && (
                  <InvButton size="small" tone="mint"
                    onClick={() => setDialog({
                      action: 'publish',
                      title: 'Publish RFQ',
                      desc: `Publish "${rfq.rfq_number}" and notify ${invitedVendors.length} invited vendor(s)?`,
                    })}>
                    Publish RFQ
                  </InvButton>
                )}
              </>
            )}
            {published && (
              <>
                {can(INV_ACTIONS.RFQ_CLOSE) && (
                  <InvButton size="small" tone="amber" variant="outlined"
                    onClick={() => setDialog({ action: 'close', title: 'Close RFQ', desc: 'Close this RFQ for new quote submissions?' })}>
                    Close
                  </InvButton>
                )}
                {can(INV_ACTIONS.RFQ_CANCEL) && (
                  <InvButton size="small" tone="red" variant="outlined"
                    onClick={() => setDialog({ action: 'cancel', title: 'Cancel RFQ', desc: 'Cancel this RFQ entirely?', destructive: true })}>
                    Cancel
                  </InvButton>
                )}
              </>
            )}
          </Box>
        )}
      />

      {justSaved && (
        <Alert severity="success" sx={{ mb: 2 }}>
          RFQ saved as <strong>Draft</strong>. Review details, then <strong>Publish RFQ</strong> to invite vendors.
        </Alert>
      )}
      {justPublished && (
        <Alert severity="success" sx={{ mb: 2 }}>
          RFQ published successfully. Invited vendors can submit quotes.
        </Alert>
      )}

      <RfqWorkflowBar status={status} />

      {summary && (
        <Alert severity={summary.insufficient_quote_count ? 'warning' : 'info'} sx={{ mb: 2.5 }}>
          {summary.submitted_quote_count ?? 0} of {summary.minimum_required_quotes ?? rfq.minimum_required_quotes} quotes received
          {summary.insufficient_quote_count && ' — minimum quote count not yet met.'}
        </Alert>
      )}

      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        sx={{ mb: 2.5, borderBottom: `1px solid ${IPD_COLORS.border}` }}
        variant="scrollable"
        scrollButtons="auto"
      >
        <Tab label="Overview" value={TAB_OVERVIEW} />
        {showQuotesTab && <Tab label={`Quotes (${quotes.length})`} value={TAB_QUOTES} />}
        {showComparisonTab && <Tab label="Comparison" value={TAB_COMPARISON} />}
        {showEvaluationTab && <Tab label="Evaluation" value={TAB_EVALUATION} />}
      </Tabs>

      {activeTab === TAB_OVERVIEW && (
        <Grid container spacing={2.5} alignItems="flex-start">
          <Grid item xs={12} md={5}>
            <SectionCard title="RFQ Details" sx={{ height: '100%' }}>
              <DetailRow label="Number">{rfq.rfq_number}</DetailRow>
              {hasDisplayValue(rfq.rfq_title || rfq.title) && (
                <DetailRow label="Title">{rfq.rfq_title || rfq.title}</DetailRow>
              )}
              {hasDisplayValue(rfq.rfq_type) && (
                <DetailRow label="Type">{rfq.rfq_type}</DetailRow>
              )}
              {hasDisplayValue(rfq.issue_date) && (
                <DetailRow label="Issue Date">{formatDate(rfq.issue_date)}</DetailRow>
              )}
              {hasDisplayValue(rfq.submission_deadline || rfq.deadline) && (
                <DetailRow label="Submission Deadline">
                  {formatDate(rfq.submission_deadline || rfq.deadline)}
                </DetailRow>
              )}
              {(rfq.minimum_required_quotes != null || rfq.required_quotes_count != null) && (
                <DetailRow label="Min. Quotes">
                  {rfq.minimum_required_quotes ?? rfq.required_quotes_count}
                </DetailRow>
              )}
              {hasDisplayValue(rfq.department_id) && (
                <DetailRow label="Department">{rfq.department_id}</DetailRow>
              )}
              {hasDisplayValue(rfq.terms_and_conditions) && (
                <DetailRow label="Terms & Conditions">{rfq.terms_and_conditions}</DetailRow>
              )}
              {hasDisplayValue(rfq.notes) && (
                <DetailRow label="Notes">{rfq.notes}</DetailRow>
              )}
            </SectionCard>

            <SectionCard title="Invited Vendors" sx={{ mt: 2.5 }}>
              {invitedVendors.length === 0 ? (
                <Typography sx={{ fontSize: 13, color: IPD_COLORS.textSecondary }}>
                  No vendors invited on this RFQ.
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {invitedVendors.map((v) => (
                    <Box
                      key={v.id}
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: 1,
                        flexWrap: 'wrap',
                      }}
                    >
                      <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{v.label}</Typography>
                      <StatusChip status="APPROVED" />
                    </Box>
                  ))}
                </Box>
              )}
            </SectionCard>
          </Grid>

          <Grid item xs={12} md={7}>
            <SectionCard title="Line Items" sx={{ height: '100%' }}>
              <Box sx={{ overflowX: 'auto' }}>
                <Table
                  size="small"
                  sx={{
                    minWidth: 560,
                    '& td, & th': {
                      borderColor: IPD_COLORS.border,
                      color: IPD_COLORS.textPrimary,
                      py: 1.25,
                      whiteSpace: 'nowrap',
                    },
                  }}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ color: IPD_COLORS.textSecondary, minWidth: 160 }}>Item</TableCell>
                      <TableCell sx={{ color: IPD_COLORS.textSecondary, minWidth: 140 }}>Specification</TableCell>
                      <TableCell sx={{ color: IPD_COLORS.textSecondary, width: 88 }}>UOM</TableCell>
                      <TableCell align="right" sx={{ color: IPD_COLORS.textSecondary, width: 72 }}>Qty</TableCell>
                      <TableCell sx={{ color: IPD_COLORS.textSecondary, width: 120 }}>Required By</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rfqItems.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} sx={{ textAlign: 'center', color: IPD_COLORS.textSecondary, py: 3 }}>
                          No items
                        </TableCell>
                      </TableRow>
                    ) : rfqItems.map((item, i) => (
                      <TableRow key={item.id || i}>
                        <TableCell sx={{ whiteSpace: 'normal', wordBreak: 'break-word', fontWeight: 600 }}>
                          {resolveItem(item)}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'normal', color: IPD_COLORS.textSecondary }}>
                          {hasDisplayValue(item.specification) ? item.specification : '—'}
                        </TableCell>
                        <TableCell>{resolveUomLabel(item, uomMap)}</TableCell>
                        <TableCell align="right">{item.required_quantity}</TableCell>
                        <TableCell>
                          {item.required_by_date ? formatDate(item.required_by_date) : '—'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </SectionCard>
          </Grid>
        </Grid>
      )}

      {activeTab === TAB_QUOTES && showQuotesTab && (
        <SectionCard title="Vendor Quotes">
          {quotes.length === 0 ? (
            <Typography sx={{ fontSize: 13, color: IPD_COLORS.textSecondary, py: 2 }}>
              No vendor quotes submitted yet.
            </Typography>
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
              <Table
                size="small"
                sx={{
                  minWidth: 640,
                  '& td, & th': { borderColor: IPD_COLORS.border, color: IPD_COLORS.textPrimary, py: 1.25 },
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ color: IPD_COLORS.textSecondary }}>Vendor</TableCell>
                    <TableCell sx={{ color: IPD_COLORS.textSecondary }}>Quote #</TableCell>
                    <TableCell sx={{ color: IPD_COLORS.textSecondary }}>Submitted</TableCell>
                    <TableCell align="right" sx={{ color: IPD_COLORS.textSecondary }}>Total</TableCell>
                    <TableCell sx={{ color: IPD_COLORS.textSecondary }}>Status</TableCell>
                    <TableCell sx={{ color: IPD_COLORS.textSecondary }}>Evaluation</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {quotes.map((q, i) => (
                    <TableRow key={q.id || q.vendor_quote_id || i}>
                      <TableCell>{q.vendor_name || q.vendor_id}</TableCell>
                      <TableCell>{q.quote_number || '—'}</TableCell>
                      <TableCell>{formatDate(q.submitted_at)}</TableCell>
                      <TableCell align="right">{formatCurrency(q.total_net_amount || q.total_value)}</TableCell>
                      <TableCell><StatusChip status={q.status} /></TableCell>
                      <TableCell>
                        {q.evaluation_status ? <StatusChip status={q.evaluation_status} /> : '—'}
                      </TableCell>
                      <TableCell>
                        {canAcceptQuote(q) && can(INV_ACTIONS.RFQ_AWARD) && (
                          <InvButton size="small" tone="mint" onClick={() => setAwardTarget(q)}>
                            Accept
                          </InvButton>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </SectionCard>
      )}

      {activeTab === TAB_COMPARISON && showComparisonTab && (
        <SectionCard title="Quote Comparison">
          <RfqQuoteComparisonTable
            comparison={comparison}
            loading={comparisonLoading}
            error={comparisonError}
            vendorDirectory={vendorDirectory}
            resolveItem={resolveItem}
          />
        </SectionCard>
      )}

      {activeTab === TAB_EVALUATION && showEvaluationTab && (
        <SectionCard title="Evaluation Summary">
          <RfqEvaluationPanel
            evaluation={evaluation}
            quotes={quotes}
            vendorDirectory={vendorDirectory}
            loading={evaluationLoading}
            error={evaluationError}
            onRefresh={() => { loadEvaluation(); loadCore(); }}
            onAward={setAwardTarget}
          />
        </SectionCard>
      )}

      <ConfirmDialog
        open={Boolean(dialog)}
        title={dialog?.title}
        description={dialog?.desc}
        destructive={dialog?.destructive}
        loading={actioning}
        onConfirm={executeAction}
        onCancel={() => setDialog(null)}
      />

      <RfqAwardDialog
        open={Boolean(awardTarget)}
        quote={awardTarget}
        onClose={() => setAwardTarget(null)}
        onSuccess={handleAwardSuccess}
      />
    </Box>
  );
}
